'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  Filter,
  FilteredCounter,
  LifecycleFilter,
  ListPageFilterPanel,
  type FilterItem,
} from '@terra/ui-design-system';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type {
  AgentExecutionStatusType,
  ExecutionTypeType,
} from '@terra/shared-schemas-ai';
import {
  isAgentExecutionStatus,
  isAgentExecutionType,
} from '../../../store/tab/agentExecutionsTabScopedFilterStore';
import { CreatedSinceFilterPill } from '../../common/CreatedSinceFilterPill';
import {
  AGENT_EXECUTION_STATUS_VALUES,
  AGENT_EXECUTION_TYPE_VALUES,
} from '../agent-executions-table/agent-executions-list.model';

type AgentExecutionsFilterPanelProps = {
  selection?: ReactNode;
  statusFilter: AgentExecutionStatusType[];
  onStatusFilterChange: (statusFilter: AgentExecutionStatusType[]) => void;
  executionType: ExecutionTypeType | null;
  onExecutionTypeChange: (executionType: ExecutionTypeType | null) => void;
  createdSince: Date | null;
  onCreatedSinceClear: () => void;
  isLifecycleEnabled: boolean;
  lifecycleState: LifecycleStateFilterDto;
  onLifecycleStateChange: (lifecycleState: LifecycleStateFilterDto) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
  filteredCount: number;
  totalCount: number;
};

export function AgentExecutionsFilterPanel({
  selection,
  statusFilter,
  onStatusFilterChange,
  executionType,
  onExecutionTypeChange,
  createdSince,
  onCreatedSinceClear,
  isLifecycleEnabled,
  lifecycleState,
  onLifecycleStateChange,
  hasActiveFilters,
  onClear,
  filteredCount,
  totalCount,
}: AgentExecutionsFilterPanelProps) {
  const tEntities = useTranslations('proxy.filteredCounter.entities');
  const tExecutions = useTranslations('designSystem.executions');
  const tStatus = useTranslations('agentExecutions.status');
  const tType = useTranslations('agentExecutions.executionType');
  const statusItems = useMemo(
    (): FilterItem[] =>
      AGENT_EXECUTION_STATUS_VALUES.map((status) => ({
        label: tStatus(status),
        value: status,
      })),
    [tStatus],
  );
  const typeItems = useMemo(
    (): FilterItem[] =>
      AGENT_EXECUTION_TYPE_VALUES.map((type) => ({
        label: tType(type),
        value: type,
      })),
    [tType],
  );

  return (
    <ListPageFilterPanel
      selection={selection}
      isClearDisabled={!hasActiveFilters}
      onClear={onClear}
      trailing={
        hasActiveFilters ? (
          <FilteredCounter
            filteredCount={filteredCount}
            totalCount={totalCount}
            entityLabel={tEntities('executions')}
          />
        ) : null
      }
    >
      <Filter
        id="agent-execution-status"
        density="compact"
        isMultiSelect
        placeholder={tExecutions('filters.status')}
        items={statusItems}
        value={statusFilter}
        onChange={(values) =>
          onStatusFilterChange(values.filter(isAgentExecutionStatus))
        }
      />
      <Filter
        id="agent-execution-type"
        density="compact"
        placeholder={tExecutions('filters.type')}
        items={typeItems}
        value={executionType ? [executionType] : []}
        onChange={(values) =>
          onExecutionTypeChange(values.find(isAgentExecutionType) ?? null)
        }
      />
      <CreatedSinceFilterPill
        createdSince={createdSince}
        onClear={onCreatedSinceClear}
      />
      {isLifecycleEnabled ? (
        <LifecycleFilter
          value={lifecycleState}
          onValueChange={onLifecycleStateChange}
        />
      ) : null}
    </ListPageFilterPanel>
  );
}
